// src/services/api.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
export const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL || "";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("idToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  },
);

export const setupResponseInterceptors = (auth, toast) => {
  if (api.interceptors.response.handlers.length > 0) {
    api.interceptors.response.eject(0);
  }
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        await auth.refreshSession();
        const newToken = localStorage.getItem("idToken");
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Transient backend errors (Lambda concurrency throttle, brief gateway issue,
      // upstream timeout). The cluster usually recovers in well under a second, so
      // a couple of quick retries with backoff turns these from "broken page" into
      // a tiny invisible delay. Mutating verbs (POST/PUT/DELETE/PATCH) are NOT
      // retried — they may not be idempotent.
      const isTransient = status === 502 || status === 503 || status === 504;
      const method = (originalRequest?.method || "get").toLowerCase();
      const isSafe = method === "get" || method === "head" || method === "options";
      if (isTransient && isSafe) {
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
        if (originalRequest._retryCount <= 2) {
          const backoffMs = originalRequest._retryCount === 1 ? 300 : 800;
          await new Promise((res) => setTimeout(res, backoffMs));
          return api(originalRequest);
        }
      }

      if (status === 403) {
        if (toast) {
          toast.error(
            "Yetkiniz Yok: Bu işlem için gerekli izne sahip değilsiniz.",
          );
        }
      }

      return Promise.reject(error);
    },
  );
};

export const organizationsAPI = {
  getAll: () =>
    api.get("/organizations", {
      headers: { "x-action": "read:organizations" },
    }),
  getById: (id) =>
    api.get(`/organizations/${id}`, {
      headers: { "x-action": "read:organizations" },
    }),
  create: (data) =>
    api.post("/organizations", data, {
      headers: { "x-action": "create:organizations" },
    }),
  update: (id, data) =>
    api.put(`/organizations/${id}`, data, {
      headers: { "x-action": "update:organizations" },
    }),
  delete: (id) =>
    api.delete(`/organizations/${id}`, {
      headers: { "x-action": "delete:organizations" },
    }),
};

export const trainersAPI = {
  getAll: (orgId, limit = 2000, lastKey) => {
    const params = { limit };
    if (lastKey) {
      params.lastKey = lastKey;
    }
    return api.get(`/organizations/${orgId}/trainers`, {
      params,
      headers: { "x-action": "read:trainers" },
    });
  },
  getById: (orgId, trainerId) =>
    api.get(`/organizations/${orgId}/trainers/${trainerId}`, {
      headers: { "x-action": "read:trainers" },
    }),
  create: (orgId, data) =>
    api.post(`/organizations/${orgId}/trainers`, data, {
      headers: { "x-action": "create:trainers" },
    }),
  update: (orgId, trainerId, data) =>
    api.put(`/organizations/${orgId}/trainers/${trainerId}`, data, {
      headers: { "x-action": "update:trainers" },
    }),
  changeRole: (orgId, trainerId, role) =>
    api.put(
      `/organizations/${orgId}/trainers/${trainerId}/role`,
      { role },
      { headers: { "x-action": "update:trainers" } },
    ),
  delete: (orgId, trainerId) =>
    api.delete(`/organizations/${orgId}/trainers/${trainerId}`, {
      headers: { "x-action": "delete:trainers" },
    }),
};

export const studentsAPI = {
  getAll: (orgId, limit = 2000, lastKey) => {
    const params = { limit };
    if (lastKey) {
      params.lastKey = lastKey;
    }
    return api.get(`/organizations/${orgId}/students`, {
      params,
      headers: { "x-action": "read:students" },
    });
  },
  getById: (orgId, studentId) =>
    api.get(`/organizations/${orgId}/students/${studentId}`, {
      headers: { "x-action": "read:students" },
    }),
  create: (orgId, data) =>
    api.post(`/organizations/${orgId}/students`, data, {
      headers: { "x-action": "create:students" },
    }),
  update: (orgId, studentId, data) =>
    api.put(`/organizations/${orgId}/students/${studentId}`, data, {
      headers: { "x-action": "update:students" },
    }),
  delete: (orgId, studentId) =>
    api.delete(`/organizations/${orgId}/students/${studentId}`, {
      headers: { "x-action": "delete:students" },
    }),
  assignTrainer: (orgId, studentId, trainerId) =>
    api.post(
      `/organizations/${orgId}/students/${studentId}/assign-trainer`,
      {
        trainerId,
      },
      { headers: { "x-action": "assign:trainers" } },
    ),
  removeTrainer: (orgId, studentId) =>
    api.post(
      `/organizations/${orgId}/students/${studentId}/remove-trainer`,
      {},
      { headers: { "x-action": "assign:trainers" } },
    ),
  addMeasurement: (orgId, studentId, data) =>
    api.post(
      `/organizations/${orgId}/students/${studentId}/measurements`,
      data,
      { headers: { "x-action": "create:measurements" } },
    ),
  getMeasurements: (orgId, studentId) =>
    api.get(`/organizations/${orgId}/students/${studentId}/measurements`, {
      headers: { "x-action": "read:measurements" },
    }),
  updateMeasurement: (orgId, studentId, measurementId, data) =>
    api.put(
      `/organizations/${orgId}/students/${studentId}/measurements/${measurementId}`,
      data,
      { headers: { "x-action": "update:measurements" } },
    ),
  deleteMeasurement: (orgId, studentId, measurementId) =>
    api.delete(
      `/organizations/${orgId}/students/${studentId}/measurements/${measurementId}`,
      { headers: { "x-action": "delete:measurements" } },
    ),
};

export const membershipPackagesAPI = {
  getAllByOrg: (orgId) =>
    api.get(`/organizations/${orgId}/packages`, {
      headers: { "x-action": "read:packages" },
    }),
  getById: (orgId, packageId) =>
    api.get(`/organizations/${orgId}/packages/${packageId}`, {
      headers: { "x-action": "read:packages" },
    }),
  create: (orgId, data) =>
    api.post(`/organizations/${orgId}/packages`, data, {
      headers: { "x-action": "create:packages" },
    }),
  update: (orgId, packageId, data) =>
    api.put(`/organizations/${orgId}/packages/${packageId}`, data, {
      headers: { "x-action": "update:packages" },
    }),
  delete: (orgId, packageId) =>
    api.delete(`/organizations/${orgId}/packages/${packageId}`, {
      headers: { "x-action": "delete:packages" },
    }),
};

export const sessionTypesAPI = {
  getAllByOrg: (orgId) =>
    api.get(`/session-types?organizationId=${orgId}`, {
      headers: { "x-action": "read:packages" },
    }),
  getById: (id) =>
    api.get(`/session-types/${id}`, {
      headers: { "x-action": "read:packages" },
    }),
  create: (orgId, data) =>
    api.post(`/session-types?organizationId=${orgId}`, data, {
      headers: { "x-action": "create:packages" },
    }),
  update: (orgId, id, data) =>
    api.put(`/session-types/${id}?organizationId=${orgId}`, data, {
      headers: { "x-action": "update:packages" },
    }),
  delete: (orgId, id) =>
    api.delete(`/session-types/${id}?organizationId=${orgId}`, {
      headers: { "x-action": "delete:packages" },
    }),
};

export const studentSubscriptionsAPI = {
  getAll: (orgId) =>
    api.get(`/student-subscriptions?org_id=${orgId}`, {
      headers: { "x-action": "read:subscriptions" },
    }),
  getById: (orgId, subscriptionId) =>
    api.get(`/student-subscriptions/${subscriptionId}?org_id=${orgId}`, {
      headers: { "x-action": "read:subscriptions" },
    }),
  getByStudent: (orgId, studentId) =>
    api.get(`/student-subscriptions?org_id=${orgId}&student_id=${studentId}`, {
      headers: { "x-action": "read:subscriptions" },
    }),
  getStudentsByMembershipPackage: (orgId, packageId) =>
    api.get(
      `/student-subscriptions/by-package?org_id=${orgId}&membership_package_id=${packageId}`,
      { headers: { "x-action": "read:subscriptions" } },
    ),
  getStudentsBySessionType: (orgId, sessionTypeId) =>
    api.get(
      `/student-subscriptions/by-session-type?org_id=${orgId}&session_type_id=${sessionTypeId}`,
      { headers: { "x-action": "read:subscriptions" } },
    ),
  create: (orgId, studentId, data) =>
    api.post(
      `/student-subscriptions?org_id=${orgId}&student_id=${studentId}`,
      data,
      { headers: { "x-action": "create:subscriptions" } },
    ),
  update: (orgId, subscriptionId, data) =>
    api.put(`/student-subscriptions/${subscriptionId}?org_id=${orgId}`, data, {
      headers: { "x-action": "update:subscriptions" },
    }),
  delete: (orgId, subscriptionId) =>
    api.delete(`/student-subscriptions/${subscriptionId}?org_id=${orgId}`, {
      headers: { "x-action": "delete:subscriptions" },
    }),
  addPayment: (orgId, subscriptionId, paymentData) =>
    api.post(
      `/student-subscriptions/${subscriptionId}/payments?org_id=${orgId}`,
      paymentData,
      { headers: { "x-action": "update:subscriptions" } },
    ),
  getPayments: (orgId, startDate, endDate) => {
    const params = { org_id: orgId, start_date: startDate, end_date: endDate };
    return api.get("/finance/student-payments", {
      params,
      headers: { "x-action": "read:finance" },
    });
  },
  deletePayment: (orgId, subscriptionId, paymentId) =>
    api.delete(
      `/student-subscriptions/${subscriptionId}/payments/${paymentId}?org_id=${orgId}`,
      { headers: { "x-action": "delete:payments" } },
    ),
};

export const exercisesAPI = {
  getAll: (orgId) =>
    api.get("/exercises", { headers: { "x-action": "read:exercises" } }),
  getById: (orgId, exerciseId) =>
    api.get(`/exercises/${exerciseId}`, {
      headers: { "x-action": "read:exercises" },
    }),
  create: (orgId, data) =>
    api.post("/exercises", data, {
      headers: { "x-action": "create:exercises" },
    }),
  update: (orgId, exerciseId, data) =>
    api.put(`/exercises/${exerciseId}`, data, {
      headers: { "x-action": "update:exercises" },
    }),
  delete: (orgId, exerciseId) =>
    api.delete(`/exercises/${exerciseId}`, {
      headers: { "x-action": "delete:exercises" },
    }),
  updateReviewStatus: (exerciseId, status) =>
    api.put(
      `/exercises/${exerciseId}/review`,
      { status },
      { headers: { "x-action": "update:exercises" } },
    ),
};

export const sessionsAPI = {
  getAll: (organizationId, params) =>
    api.get(`/organizations/${organizationId}/sessions`, {
      params,
      headers: { "x-action": "read:sessions" },
    }),
  getById: (organizationId, sessionId) =>
    api.get(`/organizations/${organizationId}/sessions/${sessionId}`, {
      headers: { "x-action": "read:sessions" },
    }),
  create: (orgId, data) =>
    api.post(`/organizations/${orgId}/sessions`, data, {
      headers: { "x-action": "create:sessions" },
    }),
  update: (organizationId, sessionId, data) =>
    api.put(`/organizations/${organizationId}/sessions/${sessionId}`, data, {
      headers: { "x-action": "update:sessions" },
    }),
  delete: (organizationId, sessionId) =>
    api.delete(`/organizations/${organizationId}/sessions/${sessionId}`, {
      headers: { "x-action": "delete:sessions" },
    }),
  getByTrainer: (orgId, trainerId, startDate, endDate) =>
    api.get(
      `/sessions/trainer?org_id=${orgId}&trainer_id=${trainerId}&start_date=${startDate}&end_date=${endDate}`,
      { headers: { "x-action": "read:sessions" } },
    ),
  getByStudent: (orgId, studentId, startDate, endDate) =>
    api.get(
      `/sessions/student?org_id=${orgId}&student_id=${studentId}&start_date=${startDate}&end_date=${endDate}`,
      { headers: { "x-action": "read:sessions" } },
    ),
  getByDateRange: (orgId, startDate, endDate) =>
    api.get(
      `/sessions/date-range?org_id=${orgId}&start_date=${startDate}&end_date=${endDate}`,
      { headers: { "x-action": "read:sessions" } },
    ),
  getBySubscriptionId: (organizationId, subscriptionId) => {
    const params = {
      org_id: organizationId,
      subscription_id: subscriptionId,
    };
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/sessions/subscription?${queryString}`, {
      headers: { "x-action": "read:sessions" },
    });
  },
  getGroupParticipants: (organizationId, sessionId) =>
    api.get(`/organizations/${organizationId}/sessions/${sessionId}/group-participants`, {
      headers: { "x-action": "read:sessions" },
    }),
  getFullDetails: (organizationId, sessionId) =>
    api.get(`/organizations/${organizationId}/sessions/${sessionId}/full-details`, {
      headers: { "x-action": "read:sessions" },
    }),
  getAvailableTimeSlots: (orgId, trainerId, date) =>
    api.get(
      `/sessions/available-slots?org_id=${orgId}&trainer_id=${trainerId}&date=${date}`,
      { headers: { "x-action": "read:sessions" } },
    ),
  // Session notes
  updateNotes: (organizationId, sessionId, notes) =>
    api.put(
      `/organizations/${organizationId}/sessions/${sessionId}`,
      {
        notes,
      },
      { headers: { "x-action": "manage:session_notes" } },
    ),
  updateStatus: (organizationId, sessionId, status) =>
    api.put(
      `/organizations/${organizationId}/sessions/${sessionId}/status`,
      {
        status,
      },
      { headers: { "x-action": "update:sessions" } },
    ),
};

// Session Exercises API
export const sessionExercisesAPI = {
  getBySession: (organizationId, sessionId) =>
    api.get(
      `/sessions/${sessionId}/exercises?organizationId=${organizationId}`,
      { headers: { "x-action": "manage:session_exercises" } },
    ),
  add: (organizationId, sessionId, exerciseId, data) =>
    api.post(
      `/sessions/${sessionId}/exercises`,
      {
        organizationId,
        exerciseId,
        ...data,
      },
      { headers: { "x-action": "manage:session_exercises" } },
    ),
  update: (organizationId, sessionId, exerciseInSessionId, data) =>
    api.put(
      `/sessions/${sessionId}/exercises/${exerciseInSessionId}`,
      {
        organizationId,
        ...data,
      },
      { headers: { "x-action": "manage:session_exercises" } },
    ),
  remove: (organizationId, sessionId, exerciseInSessionId) =>
    api.delete(`/sessions/${sessionId}/exercises/${exerciseInSessionId}`, {
      data: { organizationId },
      headers: { "x-action": "manage:session_exercises" },
    }),
};

// Session Notes API - multiple notes per session with author tracking
export const sessionNotesAPI = {
  getBySession: (organizationId, sessionId) =>
    api.get(`/sessions/${sessionId}/notes?organizationId=${organizationId}`, {
      headers: { "x-action": "manage:session_notes" },
    }),
  add: (organizationId, sessionId, data) =>
    api.post(
      `/sessions/${sessionId}/notes`,
      { organizationId, ...data },
      { headers: { "x-action": "manage:session_notes" } },
    ),
  update: (organizationId, sessionId, noteId, data) =>
    api.put(
      `/sessions/${sessionId}/notes/${noteId}`,
      {
        organizationId,
        ...data,
      },
      { headers: { "x-action": "manage:session_notes" } },
    ),
  remove: (organizationId, sessionId, noteId, requesterId) =>
    api.delete(`/sessions/${sessionId}/notes/${noteId}`, {
      data: { organizationId, requesterId },
      headers: { "x-action": "manage:session_notes" },
    }),
};

export const permissionsAPI = {
  getRolePermissions: (role) =>
    api.get(`/permissions/roles/${role}`, {
      headers: { "x-action": "read:permissions" },
    }),
  setRolePermissions: (role, permissions) =>
    api.put(
      `/permissions/roles/${role}`,
      { permissions },
      { headers: { "x-action": "manage:permissions" } },
    ),
  setRolePages: (role, pages) =>
    api.put(
      `/permissions/roles/${role}/pages`,
      { pages },
      { headers: { "x-action": "manage:permissions" } },
    ),
  getAllRoles: () =>
    api.get("/permissions/roles", {
      headers: { "x-action": "read:permissions" },
    }),
  addPermissionToRole: (role, permission) =>
    api.post(
      `/permissions/roles/${role}/add`,
      { permission },
      { headers: { "x-action": "manage:permissions" } },
    ),
  removePermissionFromRole: (role, permission) =>
    api.post(
      `/permissions/roles/${role}/remove`,
      { permission },
      { headers: { "x-action": "manage:permissions" } },
    ),
  getAvailablePermissions: () =>
    api.get("/permissions/available", {
      headers: { "x-action": "read:permissions" },
    }),
  getRoleUsers: (role) =>
    api.get(`/permissions/roles/${role}/users`, {
      headers: { "x-action": "read:permissions" },
    }),
  addUserToRole: (role, userData) =>
    api.post(`/permissions/roles/${role}/users`, userData, {
      headers: { "x-action": "manage:permissions" },
    }),
  deleteRole: (role) =>
    api.delete(`/permissions/roles/${role}`, {
      headers: { "x-action": "manage:permissions" },
    }),
  removeUserFromRole: (role, username) =>
    api.delete(`/permissions/roles/${role}/users/${username}`, {
      headers: { "x-action": "manage:permissions" },
    }),
};

export const financeAPI = {
  getSummary: (orgId, startDate, endDate) => {
    const params = { org_id: orgId, start_date: startDate, end_date: endDate };
    return api.get("/finance/summary", {
      params,
      headers: { "x-action": "read:finance" },
    });
  },
  getTrainerSalaries: (orgId, startDate, endDate) => {
    const params = { org_id: orgId, start_date: startDate, end_date: endDate };
    return api.get("/finance/trainer-salaries", {
      params,
      headers: { "x-action": "read:finance" },
    });
  },
  getTransactions: (orgId, startDate, endDate) => {
    const params = { org_id: orgId, start_date: startDate, end_date: endDate };
    return api.get("/finance/transactions", {
      params,
      headers: { "x-action": "read:finance" },
    });
  },
  getTrainerBalances: (orgId, startDate, endDate) => {
    const params = { org_id: orgId, start_date: startDate, end_date: endDate };
    return api.get("/finance/trainer-balances", {
      params,
      headers: { "x-action": "read:finance" },
    });
  },
  createTransaction: (orgId, data) =>
    api.post(
      "/finance/transactions",
      // organizationId comes LAST so an empty string in `data` (e.g.
      // TransactionModal's default `organizationId: ""` for non-salary
      // flows) cannot clobber the real org id. Without this, transactions
      // were being written under PK=`ORG#` and disappeared from listings.
      { ...data, organizationId: orgId },
      { headers: { "x-action": "manage:finance" } },
    ),
  updateTransaction: (orgId, transactionId, data) =>
    api.put(
      `/finance/transactions/${transactionId}`,
      { ...data, organizationId: orgId },
      { headers: { "x-action": "manage:finance" } },
    ),
  deleteTransaction: (orgId, transactionId) =>
    api.delete(`/finance/transactions/${transactionId}`, {
      data: { organizationId: orgId },
      headers: { "x-action": "manage:finance" },
    }),
  // Alias used by payment-deletion flows — gated by the granular `delete:payments` permission
  deletePayment: (orgId, transactionId) =>
    api.delete(`/finance/transactions/${transactionId}`, {
      data: { organizationId: orgId },
      headers: { "x-action": "delete:payments" },
    }),
  getStudentPayments: (orgId, startDate, endDate) =>
    api.get("/finance/student-payments", {
      params: { org_id: orgId, start_date: startDate, end_date: endDate },
      headers: { "x-action": "read:finance" },
    }),
};

export const fileUploadAPI = {
  getPresignedUrl: (fileType) =>
    api.post(
      "/uploads/presigned-url",
      { fileType },
      { headers: { "x-action": "upload:files" } },
    ),
  // axios put directly does not go through interceptor, it might not need action header,
  // but it's an S3 url anyway so no auth needed
  uploadFile: (url, file, contentType) =>
    axios.put(url, file, {
      headers: { "Content-Type": contentType },
    }),
};

export const analyticsAPI = {
  getDashboard: (orgId, startDate, endDate) =>
    api.get("/analytics/dashboard", {
      params: { org_id: orgId, start_date: startDate, end_date: endDate },
      headers: { "x-action": "read:finance" },
    }),
};

export const notificationsAPI = {
  getAll: (orgId) =>
    api.get("/notifications", {
      params: { org_id: orgId },
      headers: { "x-action": "read:notifications" },
    }),
  markAsRead: (orgId, notificationId, createdAt) =>
    api.post(
      `/notifications/${notificationId}/read`,
      {
        org_id: orgId,
        created_at: createdAt,
      },
      { headers: { "x-action": "read:notifications" } },
    ),
  create: (orgId, data) =>
    api.post(
      "/notifications",
      { org_id: orgId, ...data },
      { headers: { "x-action": "create:notifications" } },
    ),
};


export default api;

